import { t } from "@lingui/macro";
import { ProjectStatusEnum } from "@src/__generated__/graphql";
import {
  CapacityAllocationRepetitionTypeEnum,
  CapacityAllocationTypeEnum,
  CreateCapacityAllocationDocument,
  type CreateCapacityAllocationMutation,
  type CreateCapacityAllocationMutationVariables,
  GetAllocationModalOptionsDocument,
  type GetAllocationModalOptionsQuery,
  type GetAllocationModalOptionsQueryVariables,
  ModalCapacityAllocationProjectOptionsDocument,
  type ModalCapacityAllocationProjectOptionsQuery,
  type ModalCapacityAllocationProjectOptionsQueryVariables,
  ModalCapacityAllocationTaskOptionsDocument,
  type ModalCapacityAllocationTaskOptionsQuery,
  type ModalCapacityAllocationTaskOptionsQueryVariables,
  type Project,
  type SimpleTaskFragment,
  SqlOperator,
  type Task,
  UpdateCapacityAllocationDocument,
  UpdateCapacityAllocationForDayDocument,
  type UpdateCapacityAllocationForDayMutation,
  type UpdateCapacityAllocationForDayMutationVariables,
  UpdateCapacityAllocationItemDocument,
  type UpdateCapacityAllocationItemMutation,
  type UpdateCapacityAllocationItemMutationVariables,
  type UpdateCapacityAllocationMutation,
  type UpdateCapacityAllocationMutationVariables,
  type User,
  UserScopeEnum,
  UsersWhereColumn,
} from "@src/__generated__/urql-graphql";
import { AllocationModal } from "@src/components/modules/resource-planning/timeline/AllocationModal";
import { UsersAllocatedTimeStore } from "@src/components/ui-kit/allocated-time-indicator/store";
import type { TProgressBarSegment } from "@src/components/ui-kit/NewProgressBar";
import { resetTime } from "@src/components/ui-kit/TaskItem/TaskItemComponents";
import { client } from "@src/services/client";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import {
  type AllocationFormData,
  AllocationFormState,
} from "@src/stores/forms/AllocationFormState";
import type { ModalStore } from "@src/stores/ModalStore";
import type { AffectAllocationOccurrences } from "@src/types/planning";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { can } from "@src/utils/components/permissions";
import { getTodayStart, toApiDate } from "@src/utils/dates";
import { HUNDRED_PERCENT } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import type { UserOption } from "@src/utils/map-to-options/users";
import type { WorkTypeOption } from "@src/utils/map-to-options/workTypes";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { MinimizableDisclosureState } from "@src/utils/mobx/states/minimizable-disclosure-state";
import type { Prettify } from "@src/utils/types";
import type {
  TaskOption,
  TaskOptionLabelFormatterArgs,
} from "@src/widgets/TaskSelect/TaskSelect";
import { isBefore, isSameDay } from "date-fns";
import { cloneDeep, compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { MarkOptional } from "ts-essentials";
import type { ResourcePlanningFetchDataArgs } from "./extendables/ResourcePlanningViewStore";

export type InitialCapacityAllocationModalState = {
  userId: User["id"];
  date: Date;
  task?: SimpleTaskFragment;
};

type OnSubmitFunction = (args: ResourcePlanningFetchDataArgs) => void;
type AllocationModalOptions = Prettify<
  | {
      formData: AllocationFormData;
      affectOccurrences: AffectAllocationOccurrences;
      acceptForUserId?: User["id"];
      userId?: undefined;
      date?: undefined;
      onSubmit?: OnSubmitFunction;
    }
  | {
      formData: MarkOptional<AllocationFormData, "id">;
      affectOccurrences: AffectAllocationOccurrences;
      acceptForUserId?: User["id"];
      userId?: undefined;
      date?: undefined;
      onSubmit?: OnSubmitFunction;
    }
  | ({
      formData?: undefined;
      affectOccurrences?: undefined;
      acceptForUserId?: undefined;
      onSubmit?: OnSubmitFunction;
    } & InitialCapacityAllocationModalState)
>;

export class AllocationModalStore implements BaseStore, ModalStore {
  appStore: AppStore;
  readonly modalId = "allocationModal";

  readonly optionsWhere = new Filters<UsersWhereColumn>([
    new Filter({
      column: UsersWhereColumn.Scope,
      value: UserScopeEnum.Internal,
      options: [],
      operator: SqlOperator.Eq,
    }),
  ]);

  modalState = new MinimizableDisclosureState<AllocationModalOptions>({
    modalId: this.modalId,
    onClose: () => {
      this.form.reset({ preserveAutoFilledState: false });
      this.resetSelectedTask();
      this.initFormData = undefined;
    },
    onOpen: async (initialState) => {
      this.initFormData = cloneDeep(initialState?.formData);
      this.appStore.UIStore.dialogs.openModal({
        id: this.modalId,
        content: <AllocationModal />,
      });
      this.fetchOptions();
      if (
        !this.appStore.workspaceStore.settings
          ?.specific_time_in_planning_enabled
      ) {
        this.form.from_time.onChange(
          this.appStore.workspaceStore.defaultAllocationTime[
            CapacityAllocationTypeEnum.Task
          ].from_time,
        );
        this.form.to_time.onChange(
          this.appStore.workspaceStore.defaultAllocationTime[
            CapacityAllocationTypeEnum.Task
          ].to_time,
        );
      }

      this.form.affect_occurrences.onChange(initialState?.affectOccurrences);
      this.form.specific_time.onChange(
        !!this.appStore.workspaceStore.settings
          ?.specific_time_in_planning_enabled,
      );
      if (initialState?.formData && initialState?.affectOccurrences) {
        await this.form.convertAllocationItemToFromState(
          initialState.formData,
          initialState.affectOccurrences,
        );
      }
      if (initialState?.userId && initialState?.date) {
        await this.form.autoFillOnOpen(initialState);
      }
      this.fetchConflicts();
      if (
        initialState?.formData?.item.type !==
        CapacityAllocationTypeEnum.Imported
      )
        return;
      this.form.type.onChange(CapacityAllocationTypeEnum.Task);
    },
  });

  isSubmitting = new BooleanState(false);

  @observable isLoading = false;

  @observable.ref form = new AllocationFormState(this);

  @observable workTypeOptions: WorkTypeOption[] = [];
  @observable users: NonNullable<
    GetAllocationModalOptionsQuery["userSimpleMap"]
  > = [];
  @observable selectedTask:
    | ModalCapacityAllocationTaskOptionsQuery["task"]
    | undefined = undefined;
  @observable initFormData: AllocationModalOptions["formData"] = undefined;
  usersAllocatedTimeStore = new UsersAllocatedTimeStore([
    CapacityAllocationTypeEnum.Task,
    CapacityAllocationTypeEnum.Meeting,
    CapacityAllocationTypeEnum.TimeOff,
  ]);

  projectStatusFilterValue = [ProjectStatusEnum.Active];

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
  }

  @action.bound
  async fetchConflicts() {
    if (
      this.form.specific_time.value &&
      (this.form.from_time.value === undefined ||
        this.form.to_time.value === undefined)
    )
      return;

    const selectedUsers = Array.from(this.form.people.$.values())
      .map(({ $ }) => $.user_id.value)
      .filter((id): id is string => !!id);
    await this.usersAllocatedTimeStore.fetchConflicts({
      from: toApiDate(this.form.from_date.value),
      to: toApiDate(this.form.to_date.value),
      user_ids: selectedUsers,
      from_time: this.form.specific_time.value
        ? `${this.form.from_time.value}:00`
        : undefined,
      to_time: this.form.specific_time.value
        ? `${this.form.to_time.value}:00`
        : undefined,
      repetition_settings: this.form.repetition_settings.$.days.value,
    });
  }

  @computed get modalTitle(): string {
    return this.isEditMode
      ? t`Edit capacity allocation`
      : t`Capacity Allocation`;
  }

  @computed get isEditMode(): boolean {
    return !!this.modalState.additionalData?.formData?.id;
  }

  @computed
  get isAllocationTypeChangeDisabled(): boolean {
    if (
      this.modalState.additionalData?.formData?.item.type ===
      CapacityAllocationTypeEnum.Imported
    )
      return false;
    return this.isEditMode;
  }

  @computed
  get taskHasTrackedTime(): boolean {
    return (
      !!this.modalState.additionalData?.formData?.id &&
      !!this.modalState.additionalData.formData.tracked_for_item &&
      this.modalState.additionalData.formData.tracked_for_item > 0
    );
  }

  @computed
  get isCalendarAllocation(): boolean {
    return (
      this.initFormData?.item.type !== CapacityAllocationTypeEnum.Imported &&
      (!!this.initFormData?.item.eventLink ||
        !!this.initFormData?.item.meetLink)
    );
  }

  @computed
  get canEditTask(): boolean {
    if (this.isCalendarAllocation) return false;

    return !this.taskHasTrackedTime;
  }

  @computed get plannableUsers(): NonNullable<
    GetAllocationModalOptionsQuery["userSimpleMap"]
  > {
    return this.users.filter((user) => user.profile?.plannable);
  }
  @computed get userOptions(): UserOption[] {
    return mapToOptions.users(this.users);
  }

  @computed get plannableUserOptions(): UserOption[] {
    return mapToOptions.users(this.plannableUsers);
  }

  @computed get shouldAssignUserPositions(): boolean {
    return (
      this.form.create_task_positions.value ||
      this.form.assignment_type.value === "users"
    );
  }

  @computed get editingCurrentItem() {
    return (
      this.isEditMode &&
      (this.form.affect_occurrences.$ === "currentItem" ||
        this.form.affect_occurrences.$ === "thisDay")
    );
  }

  @computed get editingAllOccurrences() {
    if (!this.modalState?.additionalData?.formData?.item?.userItems?.length)
      return false;
    return (
      this.form.affect_occurrences.$ === "allOccurrences" &&
      this.modalState.additionalData.formData.item.userItems.length > 1
    );
  }

  @computed get today(): Date {
    return getTodayStart();
  }

  @computed get isCurrentItemInPast(): boolean {
    return (
      this.form.repetition.value ===
        CapacityAllocationRepetitionTypeEnum.Once &&
      isSameDay(this.form.from_date.value, this.form.to_date.value) &&
      isBefore(resetTime(this.form.from_date.value), this.today)
    );
  }

  @computed get thisAllocationTotalTime(): number {
    const inputTime = this.form.specific_time.value
      ? this.form.totalTimeFromSpecificTime
      : this.form.total_time.value;
    const timePerDay = this.form.specific_time.value
      ? this.form.timePerDayFromSpecificTime
      : this.form.total_time_per_day.value;
    const startDate = isBefore(resetTime(this.form.from_date.value), this.today)
      ? this.today
      : this.form.from_date.value;
    const usableAllocationOccurrenceCount =
      this.form.calculateOccurrenceForType(
        startDate,
        this.form.to_date.value,
        this.form.repetition.value,
        this.form.repetition_every.value,
        this.form.repetition_days.value ?? [],
        this.form.include_holidays.value,
      );

    if (!this.initFormData?.item || !this.isEditMode) {
      if (this.isCurrentItemInPast) return 0;

      return (
        timePerDay * usableAllocationOccurrenceCount * this.form.assigneeCount
      );
    }

    const currentItem = this.initFormData.item;

    switch (this.form.affect_occurrences.value) {
      case "currentItem":
        return currentItem.completed || this.isCurrentItemInPast
          ? 0
          : inputTime;
      case "thisDay": {
        const isInPast = isBefore(resetTime(currentItem.date), this.today);

        return isInPast
          ? 0
          : timePerDay * currentItem.seriesSummary.same_day_open_count;
      }
      default:
        return (
          timePerDay *
          (usableAllocationOccurrenceCount * this.form.assigneeCount -
            // Count of future completed allocations, we don't account those in our calculations
            currentItem.seriesSummary.futureCompletedPerUser.reduce(
              (prev, { user_id, count }) => {
                // In case someone removed user from form that had completed allocations, so they aren't
                // account for
                const isUserInForm = Array.from(this.form.people.$.values())
                  .map(({ $ }) => $.user_id.value)
                  .includes(user_id);
                return isUserInForm ? prev + count : prev;
              },
              0,
            ))
        );
    }
  }

  @computed get otherAllocationsTotalTime(): number {
    if (!this.selectedTask?.stats) return 0;
    const taskAllocatedTime = this.selectedTask.stats.allocated_time;

    if (!this.initFormData?.item || !this.isEditMode) return taskAllocatedTime;
    const currentItem = this.initFormData.item;

    switch (this.form.affect_occurrences.value) {
      case "currentItem":
        return currentItem.completed ||
          isBefore(resetTime(currentItem.date), this.today)
          ? taskAllocatedTime
          : taskAllocatedTime - currentItem.total_time;
      case "thisDay": {
        // Server returns same_day_* without a today filter, past days must stay 0
        return (
          taskAllocatedTime -
          (isBefore(resetTime(currentItem.date), this.today)
            ? 0
            : currentItem.seriesSummary.same_day_open_total_time)
        );
      }
      default:
        return (
          taskAllocatedTime - currentItem.seriesSummary.future_open_total_time
        );
    }
  }

  @computed get trackedPlusAllocated(): number {
    if (!this.selectedTask?.stats) return 0;
    return (
      this.selectedTask.stats.history_spent_time +
      this.otherAllocationsTotalTime +
      this.thisAllocationTotalTime
    );
  }

  @computed get leftToAllocate(): number {
    if (!this.selectedTask?.stats) return 0;
    return this.selectedTask.stats.planned_time - this.trackedPlusAllocated;
  }

  @computed get taskAllocationSegments(): TProgressBarSegment[] {
    if (!this.selectedTask?.stats) return [];
    const segments: TProgressBarSegment[] = [];
    const { planned_time, history_spent_time } = this.selectedTask.stats;

    segments.push({
      name: "tracked",
      percent: (history_spent_time / planned_time) * HUNDRED_PERCENT,
      bg: "teal.400",
    });

    segments.push({
      name: "other-allocations",
      percent:
        (this.otherAllocationsTotalTime / planned_time) * HUNDRED_PERCENT,
      bg: "#956FD7",
    });

    segments.push({
      name: "this-allocation",
      percent: (this.thisAllocationTotalTime / planned_time) * HUNDRED_PERCENT,
      bg: "#c4a1ff",
    });

    segments.push({
      name: "left-to-allocate",
      percent: (Math.abs(this.leftToAllocate) / planned_time) * HUNDRED_PERCENT,
      bg: this.leftToAllocate >= 0 ? "gray.200" : "red.500",
    });

    return segments;
  }

  @action.bound
  resetSelectedTask() {
    this.selectedTask = undefined;
    this.form.defaultProjectOptions = [];
    this.form.defaultBudgetItemOptions = [];
  }

  async fetchOptions() {
    this.isLoading = true;
    const { data } = await client
      .query<
        GetAllocationModalOptionsQuery,
        GetAllocationModalOptionsQueryVariables
      >(GetAllocationModalOptionsDocument, {
        where: this.optionsWhere.asWhereParam,
      })
      .toPromise();

    this.isLoading = false;
    if (!data) return;

    this.users = data.userSimpleMap;
  }

  async fetchPersonOptions({
    projectId,
    taskId,
    autoCompleteUserPositionFromTask,
  }: {
    projectId?: Project["id"];
    taskId: Task["id"];
    autoCompleteUserPositionFromTask: boolean;
  }) {
    const { data: taskData, error: taskError } = await client
      .query<
        ModalCapacityAllocationTaskOptionsQuery,
        ModalCapacityAllocationTaskOptionsQueryVariables
      >(ModalCapacityAllocationTaskOptionsDocument, {
        taskId,
      })
      .toPromise();

    if (!taskError && taskData?.task) {
      this.selectedTask = taskData.task;
      if (!projectId) {
        this.workTypeOptions = mapToOptions.workTypes(
          compact(
            taskData.task.positions.flatMap(
              (position) => position.timeTrackingWorkType,
            ),
          ),
        );
      }

      autoCompleteUserPositionFromTask &&
        this.form.autoCompleteUserPositionFromTask(taskData.task);
    }

    if (!projectId) return;

    const { data: projectData, error: projectError } = await client
      .query<
        ModalCapacityAllocationProjectOptionsQuery,
        ModalCapacityAllocationProjectOptionsQueryVariables
      >(ModalCapacityAllocationProjectOptionsDocument, {
        projectId,
      })
      .toPromise();

    if (projectError || !projectData?.projectForTask) return;
    this.workTypeOptions = mapToOptions.workTypes(
      projectData.projectForTask.availableTimeTrackingWorkTypes,
    );
  }

  onSubmit = async (updateFromCurrentItem?: boolean) => {
    const { hasError } = await this.form.validate();
    if (hasError) return;
    this.isSubmitting.on();
    this.isEditMode
      ? await this.update(updateFromCurrentItem)
      : await this.create();
    this.isSubmitting.off();
  };

  private create = () => {
    return client
      .mutation<
        CreateCapacityAllocationMutation,
        CreateCapacityAllocationMutationVariables
      >(CreateCapacityAllocationDocument, {
        input: this.form.serialize({
          action: "create",
          type: "capacityAllocation",
        }),
      })
      .toPromise()
      .then((value) => {
        if (value.data?.createCapacityAllocation)
          this.onSubmitSuccess("created");
      });
  };

  private updateCurrentItem = () => {
    return client
      .mutation<
        UpdateCapacityAllocationItemMutation,
        UpdateCapacityAllocationItemMutationVariables
      >(UpdateCapacityAllocationItemDocument, {
        input: this.form.serialize({
          action: "update",
          type: "capacityAllocation",
          affect: "currentItem",
        }),
      })
      .toPromise()
      .then((value) => {
        if (value.data?.updateCapacityAllocationItem)
          this.onSubmitSuccess("updated");
      });
  };

  private acceptCurrentUser = () => {
    return client
      .mutation<
        UpdateCapacityAllocationMutation,
        UpdateCapacityAllocationMutationVariables
      >(UpdateCapacityAllocationDocument, {
        input: this.form.serialize({
          action: "update",
          type: "capacityAllocation",
          affect: "currentUser",
        }),
      })
      .toPromise()
      .then((value) => {
        if (value.data?.updateCapacityAllocation)
          this.onSubmitSuccess("updated");
      });
  };

  private updateThisDay = () => {
    return client
      .mutation<
        UpdateCapacityAllocationForDayMutation,
        UpdateCapacityAllocationForDayMutationVariables
      >(UpdateCapacityAllocationForDayDocument, {
        input: this.form.serialize({
          action: "update",
          type: "capacityAllocation",
          affect: "thisDay",
        }),
      })
      .toPromise()
      .then((value) => {
        if (value.data?.UpdateCapacityAllocationForDay)
          this.onSubmitSuccess("updated");
      });
  };

  private updateAllOccurrences = (updateFromCurrentItem?: boolean) => {
    return client
      .mutation<
        UpdateCapacityAllocationMutation,
        UpdateCapacityAllocationMutationVariables
      >(UpdateCapacityAllocationDocument, {
        input: this.form.serialize({
          action: "update",
          type: "capacityAllocation",
          affect: updateFromCurrentItem
            ? "allOccurrencesFromToday"
            : "allOccurrences",
        }),
      })
      .toPromise()
      .then((value) => {
        if (value.data?.updateCapacityAllocation)
          this.onSubmitSuccess("updated");
      });
  };

  private update = (updateFromCurrentItem?: boolean) => {
    switch (this.form.affect_occurrences.value) {
      case "currentItem":
        return this.updateCurrentItem();
      case "thisDay":
        return this.updateThisDay();
      case "allOccurrences":
        return this.updateAllOccurrences(updateFromCurrentItem);
      case "currentUser":
        return this.acceptCurrentUser();
      default:
        return null;
    }
  };

  private onSubmitSuccess = (action: "created" | "updated"): void => {
    this.modalState.additionalData?.onSubmit?.({
      backgroundUpdate: false,
      loadForAllUsers: true,
    });
    this.appStore.UIStore.toast({
      title:
        action === "created"
          ? t`Allocation successfully created`
          : t`Allocation successfully updated`,
      status: "success",
    });
    this.modalState.onClose();
  };

  @computed
  get projectFilterValue(): string[] | undefined {
    if (!this.form.project_id.value) return undefined;
    return [this.form.project_id.value];
  }

  @computed
  get budgetItemFilterValue(): string[] | undefined {
    if (!this.form.budget_item_id.value) return undefined;
    return [this.form.budget_item_id.value];
  }

  @computed
  get defaultTaskOptions(): TaskOption[] {
    return this.form.selectedTask !== undefined ? [this.form.selectedTask] : [];
  }

  @action.bound
  handleChangeProject(value: string | undefined): void {
    this.form.project_id.onChange(value);
    this.form.budget_item_id.reset(undefined);
    this.form.task_id.reset(undefined);
    this.resetSelectedTask();
  }

  @action.bound
  handleChangeBudgetItem(value: string | undefined): void {
    this.form.budget_item_id.onChange(value);
    this.form.task_id.reset(undefined);
    this.resetSelectedTask();
  }

  @action.bound
  handleChangeTask(
    value: TaskOption["value"] | TaskOption["value"][],
    taskData: Partial<TaskOption> | Partial<TaskOption>[],
  ): void {
    const [
      taskId,
      { label, project, budgetItem, billable, timeTrackingSettings },
    ] = [
      Array.isArray(value) ? value[0] : value,
      Array.isArray(taskData) ? taskData[0] : taskData,
    ];

    this.form.task_id.onChange(taskId);
    this.form.project_id.onChange(project?.id);
    this.form.budget_item_id.onChange(budgetItem?.id);
    if (label) {
      this.form.setSelectedTask({
        id: taskId,
        name: label,
        billable: billable ?? false,
        ourWorkBudgetItem: budgetItem
          ? {
              id: budgetItem.id,
              title: budgetItem.title,
              project: budgetItem.project,
              category: budgetItem.category,
              timeTrackingSettings: timeTrackingSettings,
            }
          : undefined,
      });
      this.fetchPersonOptions({
        taskId,
        projectId: project?.id,
        autoCompleteUserPositionFromTask: true,
      });
    } else {
      this.resetSelectedTask();
    }
  }

  taskLabelFormatter(task: TaskOptionLabelFormatterArgs): string {
    if (!task.ourWorkBudgetItem) return task.name;
    return `${task.name} (${task.ourWorkBudgetItem.project.code} / ${task.ourWorkBudgetItem.title})`;
  }

  @computed
  get showProjactAndBudgetItemSelects(): boolean {
    return can("project_read_all");
  }
}
